import React, { useEffect, useState, useRef, useCallback } from 'react';
import { db } from './womenfire'; // Ensure this file points to the correct Firestore setup
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { FiShoppingCart } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import './women.css'; // Assuming you're using the same CSS
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../components/firebase/firebase';
import { isCustomerUser } from '../firebase/authActions';
import { useNavigate, useLocation } from 'react-router-dom';
import MediaAsset from '../components/MediaAsset';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';

const Men = () => {
  const [products, setProducts] = useState([]);
  const { addToCart } = useCart();
  const showToast = useToast();
  const [modalProduct, setModalProduct] = useState(null);
  const productElementRefs = useRef([]);
  const [authUser] = useAuthState(auth);
  const user = isCustomerUser(authUser) ? authUser : null;
  const navigate = useNavigate();
  const location = useLocation();

  // Handle URL-based product modals
  const handleUrlProduct = useCallback((productsList) => {
    const urlParams = new URLSearchParams(location.search);
    const productId = urlParams.get('id');
    if (productId) {
      const product = productsList.find((p) => p.id === productId);
      if (product) {
        setModalProduct(product);
      }
    }
  }, [location.search]);

  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productCollectionRef = collection(db, 'men');
        const productQuery = query(productCollectionRef, orderBy('publish_date', 'desc'));
        const productSnapshot = await getDocs(productQuery);
        const productsList = productSnapshot.docs.map(doc => ({
          id: doc.id,
          quantity: 1, // Add quantity field to each product
          ...doc.data()
        }));
        setProducts(productsList);
        handleUrlProduct(productsList);
      } catch (error) {
        console.error('Error fetching products: ', error);
      }
    };

    fetchProducts();
  }, [location.search, handleUrlProduct]);

  // Add product to the cart
  const handleAddToCart = (product) => {
    addToCart(product, 1);
    showToast(`${product.name} added to cart.`, 'success');
  };

  // Add product to cart and navigate to checkout
  const handleBuyNow = (product) => {
    if (!user) {
      showToast('Please sign in to continue with your purchase.', 'info');
      return navigate('/signin', { state: { from: `${location.pathname}${location.search}` } });
    }

    addToCart(product, 1);
    navigate('/checkout');
  };

  // Share product via WhatsApp
  const handleWhatsApp = (product) => {
    const productLink = `${window.location.origin}/#/men?id=${product.id}`; // Change here
    const whatsappMessage = `I am interested in ${product.name}, which costs Ksh ${product.price}. Check it out here: ${productLink}`;
    window.open(`https://wa.me/254745826811?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  // Handle opening product modal
  const openProductPage = (product) => {
    setModalProduct(product);
    navigate(`/men?id=${product.id}`);
  };

  // Close modal
  const closeModal = () => {
    setModalProduct(null);
    navigate('/men');
  };

  // Intersection Observer for product reveal animations
  useEffect(() => {
    const observedRefs = productElementRefs.current;
    const observers = observedRefs.map((product, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            product.classList.add('reveal');
          } else {
            product.classList.remove('reveal');
          }
        },
        { threshold: 0.2 }
      );
      if (product) observer.observe(product);
      return observer;
    });

    return () => {
      observers.forEach((observer, index) => {
        if (observedRefs[index]) observer.unobserve(observedRefs[index]);
      });
    };
  }, [products]);

  return (
    <section id="shop-section" className="new-shop-section">
      <h2 className="cormorant-garamond-regular">Men</h2>
      <div className="new-product-grid">
        {products.map((product, index) => (
          <div
            key={product.id}
            className={`new-product-item new-product-item-${index}`}
            ref={(el) => (productElementRefs.current[index] = el)}
            data-direction={index % 2 === 0 ? 'left' : 'right'}
          >
            <MediaAsset
              src={product.header_image}
              alt={product.name}
              onClick={() => openProductPage(product)}
              className="product-header-image"
            />
            <h3 className="cormorant-garamond-semibold">{product.name}</h3>
            <p className="new-product-price cormorant-garamond-regular">Ksh {product.price}</p>
            <div className="new-action-buttons">
              <button
                className="new-buy-now-btn cormorant-garamond-medium"
                onClick={() => handleBuyNow(product)}
              >
                Buy Now
              </button>
              <button
                className="new-cart-icon-btn"
                onClick={() => handleAddToCart(product)}
                aria-label={`Add ${product.name} to cart`}
              >
                <FiShoppingCart />
              </button>
              <button
                className="new-whatsapp-icon-btn"
                onClick={() => handleWhatsApp(product)}
                aria-label={`Share ${product.name} on WhatsApp`}
              >
                <FaWhatsapp />
              </button>
            </div>
          </div>
        ))}
      </div>

      {modalProduct && (
        <div className="full-page-modal">
          <div className="full-page-content">
            <span className="close-modal" onClick={closeModal}>
            <p>close</p>
            </span>
            <div className="full-page-header">
              <MediaAsset
                src={modalProduct.header_image}
                alt={modalProduct.name}
                className="full-page-main-image"
                videoControls
              />
              <h2 className="cormorant-garamond-semibold">{modalProduct.name}</h2>
              <p className="cormorant-garamond-regular">Ksh {modalProduct.price}</p>
            </div>
            <div className="full-page-gallery">
              {(modalProduct.product_images || []).map((image, i) => (
                <MediaAsset
                  key={i}
                  src={image}
                  alt={`${modalProduct.name} view ${i}`}
                  className="full-page-gallery-image"
                  videoControls
                />
              ))}
            </div>
            <div className="full-page-actions">
              <button
                className="full-page-buy-now-btn cormorant-garamond-medium"
                onClick={() => handleBuyNow(modalProduct)}
              >
                Buy Now
              </button>
              <button
                className="full-page-cart-btn"
                onClick={() => handleAddToCart(modalProduct)}
                aria-label={`Add ${modalProduct.name} to cart`}
              >
                <FiShoppingCart />
              </button>
              <button
                className="full-page-whatsapp-btn"
                onClick={() => handleWhatsApp(modalProduct)}
                aria-label={`Share ${modalProduct.name} on WhatsApp`}
              >
                <FaWhatsapp />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Men;
